import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CloseIcon from '@material-ui/icons/Close'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import SendIcon from '@material-ui/icons/Send'
import VisibilityIcon from '@material-ui/icons/Visibility'

const HMI_GREEN = '#2b7a2f'
const RISK_RED = '#a03123'

const currency = (value) => Number(value || 0).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
})

const shortDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const SummaryCard = ({ label, value, accent = HMI_GREEN }) => (
  <Card variant="outlined" style={{ minWidth: 170, flex: '1 1 170px', borderTop: `4px solid ${accent}` }}>
    <CardContent style={{ paddingBottom: 14 }}>
      <Typography variant="caption" style={{ color: '#6f796e', fontWeight: 700, letterSpacing: 0.8 }}>
        {label.toUpperCase()}
      </Typography>
      <Typography variant="h5" style={{ color: accent, fontWeight: 700, marginTop: 4 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
)

const LinesDialog = ({ company, onClose }) => (
  <Dialog open={!!company} onClose={onClose} fullWidth maxWidth="md">
    {company && (
      <>
        <DialogTitle disableTypography style={{ borderTop: `5px solid ${HMI_GREEN}`, paddingBottom: 12 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <div>
              <Typography variant="h6">{company.companyName}</Typography>
              <Typography variant="body2" style={{ color: '#6f796e' }}>
                {company.lineCount} past-due invoice{company.lineCount === 1 ? '' : 's'} · {currency(company.pastDue)}
              </Typography>
            </div>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Invoice', 'Property', 'Inv Date', 'Due Date', 'Terms', 'Original', 'Paid', 'Balance', 'Days Late'].map(header => (
                    <TableCell key={header} style={{ fontWeight: 700 }}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {company.lines.map((line, index) => (
                  <TableRow key={`${line.arNumber}-${index}`}>
                    <TableCell>{line.arNumber}</TableCell>
                    <TableCell>{line.propertyName}</TableCell>
                    <TableCell>{shortDate(line.invoiceDate)}</TableCell>
                    <TableCell>{shortDate(line.dueDate)}</TableCell>
                    <TableCell>{line.termsName || '—'}</TableCell>
                    <TableCell align="right">{currency(line.origAmount)}</TableCell>
                    <TableCell align="right">{currency(line.paidToDate)}</TableCell>
                    <TableCell align="right" style={{ fontWeight: 700 }}>{currency(line.balance)}</TableCell>
                    <TableCell align="right" style={{ color: line.daysLate > 60 ? RISK_RED : undefined }}>{line.daysLate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </>
    )}
  </Dialog>
)

// "Preview 15th Run" — stateless statement roster + guarded send, modeled on
// MaintenanceInvoicePacketPage (preview snapshot in-page, pdf as blob).
const ArStatementsPage = ({ url }) => {
  const [snapshot, setSnapshot] = useState(null)
  const [selected, setSelected] = useState([])
  const [minPastDue, setMinPastDue] = useState('')
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [sending, setSending] = useState(false)
  const [confirmSend, setConfirmSend] = useState(false)
  const [sendResult, setSendResult] = useState(null)
  const [detailCompany, setDetailCompany] = useState(null)
  const [error, setError] = useState('')
  const endpoint = `${url}/api/aspire/ar-aging/statements`

  const payload = () => ({
    companyIds: selected.length ? selected.map(company => company.companyId).filter(id => id !== null) : null,
    minPastDue: minPastDue === '' ? 0 : Number(minPastDue)
  })

  const runPreview = async (body) => {
    setLoading(true)
    setError('')
    setSendResult(null)
    try {
      const response = await axios.post(`${endpoint}/preview`, body)
      setSnapshot(response.data)
      return response.data
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'Preview failed')
      return null
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Initial load = full 15th-run roster; honor ?companyId= deep links.
    const params = new URLSearchParams(window.location.search)
    const companyId = Number(params.get('companyId'))
    runPreview({}).then(data => {
      if (data && Number.isFinite(companyId)) {
        const match = data.companies.find(company => company.companyId === companyId)
        if (match) setSelected([match])
      }
    })
    // eslint-disable-next-line
  }, [])

  const rosterOptions = snapshot?.companies || []
  const visibleCompanies = useMemo(() => {
    if (!snapshot) return []
    const wanted = new Set(selected.map(company => company.companyId))
    const min = minPastDue === '' ? 0 : Number(minPastDue)
    return snapshot.companies
      .filter(company => !wanted.size || wanted.has(company.companyId))
      .filter(company => company.pastDue >= min)
  }, [snapshot, selected, minPastDue])

  const visibleTotals = useMemo(() => ({
    companies: visibleCompanies.length,
    pastDue: visibleCompanies.reduce((sum, company) => sum + company.pastDue, 0),
    withEmail: visibleCompanies.filter(company => company.hasEmail).length,
    withoutEmail: visibleCompanies.filter(company => !company.hasEmail).length
  }), [visibleCompanies])

  const downloadPdf = async () => {
    setDownloading(true)
    setError('')
    try {
      const response = await axios.post(`${endpoint}/pdf`, payload(), { responseType: 'blob' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      link.download = `statements-${new Date().toISOString().slice(0, 10)}.pdf`
      link.click()
      URL.revokeObjectURL(link.href)
    } catch (requestError) {
      setError('PDF generation failed')
    } finally {
      setDownloading(false)
    }
  }

  const sendStatements = async () => {
    setConfirmSend(false)
    setSending(true)
    setError('')
    try {
      const response = await axios.post(`${endpoint}/send`, payload())
      setSendResult(response.data)
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Send failed')
    } finally {
      setSending(false)
    }
  }

  return (
    <Container maxWidth="lg" style={{ paddingTop: 24, paddingBottom: 48 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
        <div>
          <Typography variant="h4" style={{ fontWeight: 700, color: '#1c2419' }}>AR Statements — 15th Run</Typography>
          <Typography variant="body2" style={{ color: '#6f796e' }}>
            Every company with a past-due balance, one statement each. Stateless — always computed from the latest synced invoices.
          </Typography>
        </div>
        <Button variant="outlined" href="/aspire-ar-aging">← AR Aging</Button>
      </Box>

      {snapshot?.emailGuardActive && (
        <Alert severity="warning" style={{ marginTop: 16 }}>
          Email guard is ON — every send redirects to fivecsconsulting@gmail.com (subject shows intended recipients).
        </Alert>
      )}
      {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}

      <Paper variant="outlined" style={{ marginTop: 16, padding: 16 }}>
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
          <Autocomplete
            multiple
            style={{ minWidth: 340, flex: 1 }}
            options={rosterOptions.filter(company => company.companyId !== null)}
            value={selected}
            onChange={(event, value) => setSelected(value)}
            getOptionLabel={company => `${company.companyName} — ${currency(company.pastDue)}`}
            getOptionSelected={(option, value) => option.companyId === value.companyId}
            renderInput={params => <TextField {...params} label="Companies (empty = full roster)" variant="outlined" size="small" />}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <Chip {...getTagProps({ index })} size="small" label={option.companyName} />
            ))}
          />
          <TextField
            label="Min past due $"
            variant="outlined"
            size="small"
            type="number"
            style={{ width: 140 }}
            value={minPastDue}
            onChange={event => setMinPastDue(event.target.value)}
          />
          <Button variant="contained" style={{ background: HMI_GREEN, color: '#fff' }} disabled={loading} onClick={() => runPreview(payload())}>
            {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Refresh preview'}
          </Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={downloading || !visibleCompanies.length} onClick={downloadPdf}>
            {downloading ? 'Building…' : 'Download PDF'}
          </Button>
          <Button
            variant="contained"
            style={{ background: visibleCompanies.length ? RISK_RED : undefined, color: '#fff' }}
            startIcon={<SendIcon />}
            disabled={sending || !visibleCompanies.length}
            onClick={() => setConfirmSend(true)}
          >
            {sending ? 'Sending…' : `Email ${visibleTotals.withEmail} statement${visibleTotals.withEmail === 1 ? '' : 's'}`}
          </Button>
        </Box>
      </Paper>

      {snapshot && (
        <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 16 }}>
          <SummaryCard label="Companies" value={visibleTotals.companies} />
          <SummaryCard label="Total past due" value={currency(visibleTotals.pastDue)} accent={RISK_RED} />
          <SummaryCard label="With email" value={visibleTotals.withEmail} />
          <SummaryCard label="No email (manual)" value={visibleTotals.withoutEmail} accent="#d86f00" />
        </Box>
      )}

      {sendResult && (
        <Alert severity={sendResult.failed ? 'warning' : 'success'} style={{ marginTop: 16 }}>
          Sent {sendResult.sent} · Skipped {sendResult.skipped} (no email) · Failed {sendResult.failed}
          {sendResult.emailGuardActive ? ' — guard redirect active' : ''}
        </Alert>
      )}

      {snapshot && (
        <TableContainer component={Paper} variant="outlined" style={{ marginTop: 16 }}>
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: '#f1ecdd' }}>
                {['Company', 'Billing contacts', 'Invoices', 'Worst days late', 'Past due', ''].map(header => (
                  <TableCell key={header} style={{ fontWeight: 700 }}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCompanies.map(company => (
                <TableRow key={company.companyId ?? company.companyName} hover>
                  <TableCell style={{ fontWeight: 600 }}>
                    {company.companyName}
                    {!company.hasEmail && <Chip size="small" label="no email" style={{ marginLeft: 8, background: '#f7e5c4' }} />}
                  </TableCell>
                  <TableCell>
                    {(company.billingContacts || []).slice(0, 2).map(contact => contact.name).join(', ')}
                    {(company.billingContacts || []).length > 2 ? ` +${company.billingContacts.length - 2}` : ''}
                  </TableCell>
                  <TableCell>{company.lineCount}</TableCell>
                  <TableCell style={{ color: company.worstDaysLate > 60 ? RISK_RED : undefined }}>{company.worstDaysLate}</TableCell>
                  <TableCell style={{ fontWeight: 700, color: RISK_RED }}>{currency(company.pastDue)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="View invoices" onClick={() => setDetailCompany(company)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!visibleCompanies.length && (
                <TableRow><TableCell colSpan={6} align="center" style={{ color: '#6f796e' }}>No past-due companies match.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loading && !snapshot && (
        <Box display="flex" justifyContent="center" style={{ marginTop: 48 }}><CircularProgress /></Box>
      )}

      <LinesDialog company={detailCompany} onClose={() => setDetailCompany(null)} />

      <Dialog open={confirmSend} onClose={() => setConfirmSend(false)}>
        <DialogTitle>Send statements?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Emails one statement PDF to each of the {visibleTotals.withEmail} companies with billing-contact emails
            ({visibleTotals.withoutEmail} without email will be skipped).
            {snapshot?.emailGuardActive && ' Guard is ON — everything redirects to fivecsconsulting@gmail.com.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(false)}>Cancel</Button>
          <Button style={{ color: RISK_RED, fontWeight: 700 }} onClick={sendStatements}>Send</Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

const mapStateToProps = (state) => ({ url: state.url })

export default connect(mapStateToProps)(ArStatementsPage)
